import React, { useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";

import {
  Button,
  Confirm,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";

import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { CancelIcon, CloseIcon, DeleteIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import {
  deactivateReloadYardAccounts,
  doUpdateReloadYardAccount,
  sendEmails,
} from "../../../managerApp/resources/customers/customerUtils";
import { emailDeleteHTML } from "./emailDeleteHTML";

const useStyles = makeStyles(
  (theme) => ({
    dialogTitle: {
      display: "flex",
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      justifyContent: "space-between",
    },
    title: {
      paddingTop: theme.spacing(1),
    },
    dialogContent: {
      // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
      minHeight: "50vh",
    },
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

export const DeleteYardButton = ({
  record,
  appUser,
  setAppUser,
  className,
  ...props
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloadYards, setReloadYards] = useState(
    appUser.reloadYardAccounts.items.map((item) => item.reloadYard),
  );
  const [selectedYardId, setSelectedRYardId] = useState("");

  const handleDialogOpen = () => setIsOpen(true);
  const handleDialogClose = () => setIsOpen(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      debugger;
      // Update customer active = "false", tirggers lambda function "/processCustomer"
      const reloadYardAccount = appUser.reloadYardAccounts.items.find(
        (account) => account.reloadYardId === selectedYardId,
      );
      reloadYardAccount.active = "false";
      reloadYardAccount.firstName = appUser.firstName;
      await doUpdateReloadYardAccount(reloadYardAccount);

      // send silently don't await
      sendEmails(reloadYardAccount, emailDeleteHTML);

      // update appUser
      const newReloadYardAccounts = appUser.reloadYardAccounts.items.filter(
        (account) => account.reloadYardId !== selectedYardId,
      );
      setReloadYards(newReloadYardAccounts.map((item) => item.reloadYard));
      setAppUser({
        ...appUser,
        reloadYardAccounts: {
          items: newReloadYardAccounts,
        },
      });

      notify("Removed access successfully", "success");
    } catch (error) {
      logError(
        "custoemrApp:customers:DeleteYardButton:handleDelete",
        error,
        notify,
      );
    } finally {
      setLoading(false);
      setIsOpen(false);
      refresh();
    }
  };

  return (
    <>
      <Button
        {...props}
        label="Remove Transload"
        disabled={loading}
        onClick={handleDialogOpen}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Dialog open={isOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography className={classes.title} variant="h6">
            Remove Transload Access
          </Typography>
          <IconButton aria-label="close" onClick={handleDialogClose}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="overline" gutterBottom>
                Select a transload to remove:
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                id="site-select"
                label="Transload"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={selectedYardId.id}
                onChange={(event) => setSelectedRYardId(event.target.value)}
                helperText="Cannot remove access to a transload being currently managed."
              >
                {reloadYards.map((yard) => (
                  <MenuItem
                    key={yard.id}
                    value={yard.id}
                    disabled={yard.id === appUser.reloadYardId}
                  >
                    {yard.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button label="Close" color="default" onClick={handleDialogClose}>
            <CancelIcon />
          </Button>
          <Button
            label="Remove Access"
            color="primary"
            disabled={!selectedYardId || loading}
            onClick={handleDelete}
          >
            <DeleteIcon />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const DeleteAccountButton = ({ record, className, ...props }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isDeleteDisabled = () => {
    return loading;
  };

  const handleDialogOpen = () => setIsOpen(true);
  const handleDialogClose = () => setIsOpen(false);

  const handleDelete = async (event) => {
    setLoading(true);
    try {
      debugger;
      // Update customer active = "false", tirggers lambda function "/processCustomer"
      const responses = await Promise.allSettled(
        deactivateReloadYardAccounts(record?.reloadYardAccount?.items),
      );
      const resError = responses.find((item) => item.status === "rejected");
      if (resError) {
        logError(
          "custoemrApp:customers:DeepDeleteButton:handleDelete:response",
          resError,
          false,
        );
        throw resError;
      }

      // send silently don't await
      const newReloadYardAccounts = record?.reloadYardAccounts?.items.map(
        (item) => {
          return { ...item, firstName: record.firstName };
        },
      );
      sendEmails(newReloadYardAccounts, emailDeleteHTML);

      notify("Account deleted successfully", "success");
      redirect("/logout");
    } catch (error) {
      logError(
        "custoemrApp:customers:DeleteAccountButton:handleDelete",
        error,
        notify,
      );
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        {...props}
        label="Delete My Account"
        disabled={isDeleteDisabled()}
        onClick={handleDialogOpen}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={loading}
        title={"Delete Confirmation"}
        content={
          <>
            Are you sure you want to delete your account? <br />
            You will lose access to all transloads! <br />
            Use DELETE TRANSLOAD to remove access to a single transload.
          </>
        }
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};
